import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Clock, Users } from 'lucide-react';

const TrailCard = ({ trail }) => {
  // Generate route based on trail id
  const getTrailRoute = (trailId) => {
    switch (trailId) {
      case 'od-glecera-do-grebaje':
        return '/trails/od-glecera-do-grebaje';
      case 'legende-brskova':
        return '/trails/legende-brskova';
      case 'kanjon-tare':
        return '/trails/kanjon-tare';
      case 'tri-jezera':
        return '/trail-details-in-the-embrace-of-forest-and-three-lakes';
      case 'zivot-kroz-vekove-golija':
        return '/trails/zivot-kroz-vekove-golija';
      case 'listening-to-medieval-serbia':
        return '/trail-details-listening-to-the-pulse-of-medieval-serbia';
      case 'trail-details-and-there-was-water-life-of-people-in-mountains-and-water':
        return '/trail-details-and-there-was-water-life-of-people-in-mountains-and-water';
      case 'trail-details-once-upon-a-time-myths-and-legends-of-mountain-people':
        return '/trail-details-once-upon-a-time-myths-and-legends-of-mountain-people';
      default:
        return '/trail-details';
    }
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty?.toLowerCase()) {
      case 'easy':
        return 'text-green-600 bg-green-50';
      case 'moderate':
        return 'text-yellow-600 bg-yellow-50';
      case 'challenging':
        return 'text-red-600 bg-red-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };

  const getThemeColor = (theme) => {
    switch (theme) {
      case 'Natural Heritage':
        return 'text-green-700 bg-green-100';
      case 'Cultural Heritage':
        return 'text-blue-700 bg-blue-100';
      case 'Medieval Heritage':
        return 'text-purple-700 bg-purple-100';
      case 'Water Heritage':
        return 'text-cyan-700 bg-cyan-100';
      case 'Mythology Heritage':
        return 'text-indigo-700 bg-indigo-100';
      case 'Religious Sites':
        return 'text-purple-700 bg-purple-100';
      case 'River Valleys':
        return 'text-teal-700 bg-teal-100';
      case 'Forest Heritage':
        return 'text-emerald-700 bg-emerald-100';
      default:
        return 'text-gray-700 bg-gray-100';
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
      <div className="aspect-w-16 aspect-h-9">
        <img
          src={trail?.image}
          alt={trail?.imageAlt || `${trail?.name} trail scenery`}
          className="w-full h-48 object-cover"
        />
      </div>
      
      <div className="p-6">
        {/* Country Flag and Name */}
        <div className="flex items-center gap-2 mb-3">
          <img
            src={trail?.countryFlag}
            alt={trail?.countryFlagAlt || `${trail?.country} flag`}
            className="w-6 h-4 object-cover rounded shadow-sm"
          />
          <span className="text-sm font-medium text-gray-600">{trail?.country}</span>
        </div>

        {/* Trail Name */}
        <h3 className="text-xl font-bold text-gray-900 mb-3 line-clamp-2">
          {trail?.name}
        </h3>

        {/* Theme Badge */}
        <div className="mb-4">
          <span className={`inline-flex px-3 py-1 rounded-full text-xs font-medium ${getThemeColor(trail?.theme)}`}>
            {trail?.theme}
          </span>
        </div>

        {/* Trail Stats */}
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 text-gray-400 font-bold">!</div>
            <div>
              <p className="text-xs text-gray-500">Težina</p>
              <p className={`text-sm font-semibold px-2 py-1 rounded ${getDifficultyColor(trail?.difficulty)}`}>
                {trail?.difficulty}
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <MapPin className="w-4 h-4 text-gray-400" />
            <div>
              <p className="text-xs text-gray-500">Dužina</p>
              <p className="text-sm font-semibold text-gray-900">{trail?.length}</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-gray-400" />
            <div>
              <p className="text-xs text-gray-500">Trajanje</p>
              <p className="text-sm font-semibold text-gray-900">{trail?.duration}</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4 text-gray-400" />
            <div>
              <p className="text-xs text-gray-500">Grupa</p>
              <p className="text-sm font-semibold text-gray-900">Svi nivoi</p>
            </div>
          </div>
        </div>

        {/* Description */}
        <p className="text-gray-600 text-sm mb-4 line-clamp-3">
          {trail?.description}
        </p>

        {/* Highlights */}
        {trail?.highlights && trail?.highlights?.length > 0 && (
          <div className="mb-4">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
              Ključne atrakcije
            </p>
            <div className="flex flex-wrap gap-1">
              {trail?.highlights?.slice(0, 3)?.map((highlight, index) => (
                <span
                  key={index}
                  className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded-full"
                >
                  {highlight}
                </span>
              ))}
              {trail?.highlights?.length > 3 && (
                <span className="text-xs text-gray-500 px-2 py-1">
                  +{trail?.highlights?.length - 3} more
                </span>
              )}
            </div>
          </div>
        )}

        {/* Action Button */}
        <Link
          to={getTrailRoute(trail?.id)}
          className="block w-full bg-primary hover:bg-primary-dark text-white font-semibold py-3 px-4 rounded-lg text-center transition-colors duration-200"
        >
          Istraži stazu
        </Link>
      </div>
    </div>
  );
};

export default TrailCard;